export type AttachmentFile = { name: string; url: string };

export type DrawingPdf = AttachmentFile;

const UUID_PREFIX_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;

const DEFAULT_EXT = "pdf";

const extRegex = (ext: string) => new RegExp(`\\.${ext}$`, "i");

export const fileNameFromUrl = (url: string) => {
  const last = url.split(/[?#]/)[0].split("/").pop() || "";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

export const attachmentKey = (value: string, ext: string = DEFAULT_EXT) =>
  value
    .replace(/\s+/g, " ")
    .trim()
    .replace(extRegex(ext), "")
    .trim()
    .toLowerCase();

export function toAttachmentList(
  attachments: any,
  ext: string = DEFAULT_EXT,
): AttachmentFile[] {
  if (!Array.isArray(attachments)) return [];
  const extRe = extRegex(ext);
  const list: AttachmentFile[] = [];
  for (const entry of attachments) {
    if (!entry) continue;
    const url = typeof entry === "string" ? entry : entry?.url;
    if (typeof url !== "string" || !url.trim()) continue;
    const rawName =
      typeof entry === "string"
        ? fileNameFromUrl(url)
        : typeof entry?.name === "string" && entry.name.trim()
          ? entry.name
          : fileNameFromUrl(url);
    const name = UUID_PREFIX_RE.test(rawName)
      ? rawName.replace(UUID_PREFIX_RE, "").trim()
      : rawName.trim();
    if (!name) continue;
    if (!extRe.test(name) && !extRe.test(fileNameFromUrl(url))) {
      continue;
    }
    list.push({ name, url });
  }
  return list;
}

export function buildAttachmentIndex(
  attachments: any,
  ext: string = DEFAULT_EXT,
): Map<string, AttachmentFile> {
  const index = new Map<string, AttachmentFile>();
  for (const file of toAttachmentList(attachments, ext)) {
    const key = attachmentKey(file.name, ext);
    if (!key || index.has(key)) continue;
    index.set(key, file);
  }
  return index;
}

export function lookupAttachment(
  index: Map<string, AttachmentFile>,
  text?: string | null,
  ext: string = DEFAULT_EXT,
): AttachmentFile | null {
  if (!text || typeof text !== "string") return null;
  const key = attachmentKey(text, ext);
  if (!key) return null;
  return index.get(key) ?? null;
}

export function findMatchingAttachment(
  attachments: any,
  name?: string | null,
  ext: string = DEFAULT_EXT,
): AttachmentFile | null {
  if (!name) return null;
  return lookupAttachment(buildAttachmentIndex(attachments, ext), name, ext);
}

export const drawingPdfKey = (value: string) => attachmentKey(value);

export const toDrawingPdfList = (attachments: any) =>
  toAttachmentList(attachments);

export const buildDrawingPdfIndex = (attachments: any) =>
  buildAttachmentIndex(attachments);

export const lookupDrawingPdf = (
  index: Map<string, DrawingPdf>,
  text?: string | null,
) => lookupAttachment(index, text);

export const findMatchingPdf = (
  attachments: any,
  drawingName?: string | null,
) => findMatchingAttachment(attachments, drawingName);
